// ─────────────────────────────────────────────────────────────────────────────
// usePulseSchedule — GET /api/rexy/pulse-schedule. Shared core (2026-07-31).
//
// Her proof of life: the two next fires, then the latest <=12 accrued slots
// newest-first. A pulse that never landed reads 'missed' — it is never folded
// into 'complete' and never silently omitted.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { makeTimeFormatter } from './timeFormat';
import { PULSE_SCHEDULE } from './endpoints';

// ── contract (pinned; the backend builds to exactly this) ────────────────────

export type PulseRow = {
  slot_id: string;
  scheduled_for: string; // ISO w/ offset
  executed_at: string | null;
  result: 'green' | 'red';
  status: string; // raw, e.g. complete / missed / partial / ledger_write_failed / tick_error:X
  events_written: number;
  failures: number;
};

export type PulseSchedule = {
  ledger_status: 'ok' | 'fresh' | 'unreadable';
  pulses: PulseRow[]; // EXACTLY the latest <=12 accrued slots, NEWEST-FIRST
  next_pulses: string[]; // two future ISO timestamps
  pending_retries: number;
  abandoned: number;
  malformed: number;
  timezone: string; // IANA name — the AUTHORITATIVE world clock every label reads
};

export type PulseScheduleResponse = {
  success: boolean;
  schedule: PulseSchedule;
  // `reports` is present in the response and deliberately not typed/read here.
  error?: string;
};

export type PulseFeed = {
  schedule: PulseSchedule | null;
  err: string | null;
  hourLabel: (iso: string) => string;
};

export function usePulseSchedule(): PulseFeed {
  const [schedule, setSchedule] = useState<PulseSchedule | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Built from the CURRENT payload's zone, keyed on the zone itself — so when the
  // owner changes the world clock on the settings page, the next poll returns a
  // payload carrying the NEW zone and the labels move with it. No extra
  // subscription: the zone travels with the data.
  const hourLabel = useMemo(
    () => makeTimeFormatter(schedule?.timezone), [schedule?.timezone]);

  useEffect(() => {
    const fetchSchedule = () => {
      fetch(PULSE_SCHEDULE, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (j && j.success && j.schedule) {
            setSchedule((j as PulseScheduleResponse).schedule);
            setErr(null);
          } else {
            setErr((j && j.error) || 'unavailable');
          }
        })
        .catch((e) => setErr(String(e)));
    };
    fetchSchedule();
    const poll = setInterval(fetchSchedule, POLL_MS);
    return () => clearInterval(poll);
  }, []);

  return { schedule, err, hourLabel };
}
